import type { ReactNode } from "react";

import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";

interface AuthShellProps {
  /**
   * Ties `<main aria-labelledby>` to the page's `<h1 id>`. Each auth page
   * keeps its own heading id so existing `aria-labelledby` selectors and
   * accessible-name lookups resolve unchanged.
   */
  headingId: string;
  /** The page's primary heading text (rendered into the `<h1 id={headingId}>`). */
  heading: ReactNode;
  /** Optional coral overline above the heading (e.g. "Partner sign-in", "Welcome"). */
  overline?: ReactNode;
  /** Tailwind classes for the overline `<p>`. Login uses an 11px size; the
   *  onboarding/complete shells use `text-sm`. Defaults to the 11px variant. */
  overlineClassName?: string;
  /** Tailwind classes for the `<h1>`. Login's card uses `text-3xl`; the
   *  centered cards use `text-2xl`. Defaults to `text-2xl`. */
  headingClassName?: string;
  /** Optional subheading paragraph under the heading. */
  subheading?: ReactNode;
  /** Whether to render the ResetData wordmark above the header. Login,
   *  onboarding, complete and request-access show it; forgot/set-password
   *  do not. */
  showLogo?: boolean;
  /** `space-y-*` rhythm on the header block. Defaults to `space-y-2`. */
  headerClassName?: string;
  /** Width cap on the card. The single-column auth cards use `max-w-md`;
   *  the wizard cards use `max-w-lg`. Defaults to `max-w-md`. */
  maxWidthClassName?: string;
  /** `space-y-*` rhythm between the header block and the card body. Most
   *  pages use `space-y-6`; the terse "missing token" / confirmation
   *  variants use `space-y-4`. Defaults to `space-y-6`. */
  bodyClassName?: string;
  /** Centering wrapper around the card. Single-column pages use
   *  `min-h-screen flex items-center justify-center p-4`; the wizards use
   *  `px-4 py-10`. Defaults to the single-column variant. */
  wrapperClassName?: string;
  /**
   * Omit the outer centering wrapper entirely. Login's split-screen layout
   * provides its own centering grid cell, so it renders the card `bare`.
   */
  bare?: boolean;
  children: ReactNode;
}

/**
 * Shared chrome for the unauthenticated auth pages (CHP-551): the centered
 * card frame, ResetData wordmark, and heading block that login, set-password,
 * forgot-password, the onboarding wizard, the Registration-Complete screen and
 * request-access all duplicated verbatim.
 *
 * STRICTLY presentational. It owns only the surrounding chrome — the outer
 * centering wrapper, the `<main>` card, the optional logo, and the
 * overline/heading/subheading header block. Each page's own form, copy, links,
 * progress strip and behaviour live entirely in `children`, byte-for-byte as
 * before. Per-page differences (card width, spacing rhythm, heading size,
 * whether the logo shows, the login split-screen) are parameterised via props
 * rather than forced identical.
 */
export function AuthShell({
  headingId,
  heading,
  overline,
  overlineClassName = "text-[11px] font-semibold uppercase tracking-[0.18em] text-primary",
  headingClassName = "text-2xl font-semibold tracking-tight",
  subheading,
  showLogo = false,
  headerClassName = "space-y-2",
  maxWidthClassName = "max-w-md",
  bodyClassName = "space-y-6",
  wrapperClassName = "min-h-screen flex items-center justify-center p-4",
  bare = false,
  children,
}: AuthShellProps) {
  const card = (
    <main
      aria-labelledby={headingId}
      className={cn(
        "w-full bg-card text-card-foreground rounded-xl border shadow-lifted p-8",
        maxWidthClassName,
        bodyClassName,
      )}
    >
      <div className={headerClassName}>
        {showLogo ? <Logo className="mb-4 h-6" /> : null}
        {overline ? <p className={overlineClassName}>{overline}</p> : null}
        <h1 id={headingId} className={headingClassName}>
          {heading}
        </h1>
        {subheading ? <p className="text-sm text-muted-foreground">{subheading}</p> : null}
      </div>
      {children}
    </main>
  );

  if (bare) return card;

  return <div className={wrapperClassName}>{card}</div>;
}
